<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">
          Generate Docusign
        </h1>
      </template>
      <template #actions>
        <page-header-action tag="router-link" :to="`/jobs/${job?.identifier}`">
          Back to job
        </page-header-action>
        <page-header-action
          @click.prevent="openGenerateHireAgreementModal"
          v-if="!!hire"
        >
          Generate Hire Agreement
        </page-header-action>
        <page-header-action @click.prevent="openGenerateStorageAgreementModal">
          Generate Storage Agreement
        </page-header-action>
      </template>
    </page-header>

    <div class="card" v-if="!loading">
      <div class="card-body">
        <h3>Documents</h3>
        <hr />
        <div class="col-md-12">
          <div class="row">
            <div class="col-md-6">
              <inputbox
                v-model="form.name"
                name="name"
                label="Name"
                :error-message="form.errors.get('name')"
                helper="Please enter the name"
                :show-error="form.errors.has('name')"
              ></inputbox>
            </div>
            <div class="col-md-6">
              <inputbox
                v-model="form.email"
                name="email"
                label="email"
                :error-message="form.errors.get('email')"
                helper="Please enter the email"
                :show-error="form.errors.has('email')"
              ></inputbox>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <inputbox
                name="subject"
                label="Subject"
                v-model="form.subject"
                helper="Please enter the subject of the email address."
              ></inputbox>
            </div>
          </div>
          <div class="row">
            <table class="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>
                    <label for="selectAllCheckbox">
                      <input
                        type="checkbox"
                        id="selectAllCheckbox"
                        v-model="selectAll"
                        @change="toggleSelectAll"
                      />
                      Select All
                    </label>
                  </th>
                  <th>Document Type</th>
                  <th>Document Description</th>
                  <th>Uploaded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody v-if="documents?.length">
                <tr v-bind:key="index" v-for="(document, index) in documents">
                  <td>
                    <input
                      type="checkbox"
                      v-model="selectedDocuments"
                      :value="document"
                    />
                  </td>
                  <td>
                    {{
                      document.type ? document.type.label : document.file_name
                    }}
                  </td>
                  <td>{{ document.description }}</td>
                  <td>{{ document.created_at }}</td>
                  <td>
                    <button
                      class="btn btn-primary"
                      type="button"
                      @click="download(document)"
                    >
                      view
                    </button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td class="text-center" colspan="5">
                    No documents found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="card-footer text-right">
        <form-button :indicator="form.processing()" @clicked="send">
          <i class="icon icon-save"></i> send
        </form-button>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>
    <generate-hire-agreement
      v-if="incident && hire"
      :incident="incident"
      :hire="hire"
      :show="hireModals.generate"
      v-on:close="closeGenerateHireAgreementModal"
      v-on:generated="refresh"
    ></generate-hire-agreement>
    <generate-storage-agreement
      v-if="incident"
      :incident="incident"
      :show="storageModals.generate"
      v-on:close="closeGenerateStorageAgreementModal"
      v-on:generated="refresh"
    ></generate-storage-agreement>
  </div>
</template>

<script>
import GenerateHireAgreement from '@/components/Modules/Job/Children/GenerateHireAgreement';
import GenerateStorageAgreement from '@/components/Modules/Job/Children/GenerateStorageAgreement';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    id: {
      type: String
    }
  },

  components: {
    GenerateHireAgreement,
    GenerateStorageAgreement,
    PageHeader,
    PageHeaderAction
  },

  data() {
    return {
      hireModals: {
        generate: false
      },
      storageModals: {
        generate: false
      },
      job: null,
      hire: null,
      incident: null,
      documents: [],
      selectAll: false,
      selectedDocuments: [],
      form: new window.Form({
        name: null,
        email: null,
        subject: null
      }),
      loading: true
    };
  },

  computed: {},

  mounted() {
    this.$store.commit('JobTypes/retrieve');
    this.$store.commit('DocumentTypes/retrieve');

    if (this.id) {
      window.Ajax.get(`/jobs/${this.id}?parts=jobable,type`)
        .then(({ data }) => {
          this.job = data;

          if (this.job?.envelope) {
            this.sendToRoute();
          }

          if (
            this.job.type.name === 'hire_delivery' ||
            this.job.type.name === 'hire_collection'
          ) {
            this.hire = this.job.jobable;
            this.incident = this.job.jobable.incident;
          } else if (
            this.job.type.name === 'client_delivery' ||
            this.job.type.name === 'client_collection'
          ) {
            this.hire = null;
            this.incident = this.job.jobable;
          }

          if (this.incident) {
            this.setClientDetails();
            this.getIncidentDocuments();
          }

          this.loading = false;
        })
        .catch(err => {
          window.toastify.error(
            'Oops',
            'An error occurred while loading the job'
          );
        });
    } else {
      this.loading = false;
    }
  },

  methods: {
    refresh() {
      this.hireModals.generate = false;
      this.storageModals.generate = false;
      this.getIncidentDocuments();
      window.toastify.success(
        'Success!',
        'The document was uploaded successfully!'
      );
    },

    setClientDetails() {
      this.form.name = this.incident.client.full_name;
      this.form.email = this.incident.client.email_address;
    },

    getIncidentDocuments() {
      window.Ajax.get(
        `/incidents/${this.incident.identifier}/documents?filter[types]=credit_hire_agreement,credit_storage_agreement,direct_or_cross_hire_agreement,mitigation_statement`
      )
        .then(response => {
          this.documents = response.data;

          this.getStaticDocuments();
        })
        .catch(error => {});
    },

    getStaticDocuments() {
      window.Ajax.get(`/static-documents`)
        .then(response => {
          this.documents.push(...response.data);
        })
        .catch(error => {});
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedDocuments = this.documents.slice();
      } else {
        this.selectedDocuments = [];
      }
    },

    openGenerateHireAgreementModal() {
      if (this.incident && this.hire) {
        this.hireModals.generate = true;
      }
    },

    closeGenerateHireAgreementModal() {
      this.hireModals.generate = false;
    },

    openGenerateStorageAgreementModal() {
      if (this.incident) {
        this.storageModals.generate = true;
      }
    },

    closeGenerateStorageAgreementModal() {
      this.storageModals.generate = false;
    },

    download(document) {
      window.open(document.url, '_blank');
    },

    send() {
      this.form.errors.clear();

      const selectedDocumentsData = this.selectedDocuments.map(document => ({
        id: document.identifier,
        type: document.type?.name
      }));

      selectedDocumentsData.forEach((data, index) => {
        this.form.addData(`documents[${index}][id]`, data.id);
        this.form.addData(`documents[${index}][type]`, data.type ?? '');
      });

      this.form
        .post(`/jobs/${this.job.identifier}/envelopes`)
        .then(response => {
          window.toastify.success('Success!', 'Envelope created successfully!');
          this.sendToRoute();
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Oops, there was an error generating the document'
          );
        });
    },

    sendToRoute() {
      this.route(`/jobs/${this.job.identifier}`);
    }
  }
};
</script>
